import { useContext, useEffect, useState } from "react";

import { Container, Grid2Props as GridProps } from "@mui/material";

import {
  ActionIcon,
  Button,
  Checkbox,
  CopyButton,
  Divider,
  Group,
  Image,
  Modal,
  Popover,
  Radio,
  Select,
  SelectProps,
  Table,
  Text,
  Textarea,
  TextInput,
  TextProps,
  Tooltip,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";

import { Carousel } from "@mantine/carousel";

import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCircle,
  IconCircleFilled,
  IconEdit,
  IconEye,
  IconPhoto,
  IconPlus,
  IconReload,
  IconTrash,
  IconUpload,
  IconX,
} from "@tabler/icons-react";

import dayjs from "dayjs";
import { GridRenderCellParams } from "@mui/x-data-grid";

import { Colors, YesOrNoList } from "../../Lib/data";
import {
  CourseProps,
  PaginationProps,
  PlanProps,
  TeacherProps,
} from "../../Lib/types";

import DatagridDisplay from "../../Components/DatagridDisplay";
import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";
import { Endpoints } from "../../Lib/endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  generateRandomString,
  getEllipsisFromString,
  showValidationErrors,
} from "../../Lib/methods";
import { PaginatedResponse } from "../../Lib/responses";
import {
  PerformRequest,
  UploadFiles,
  usePerformRequest,
} from "../../Lib/usePerformRequest";
import { AppContext } from "../Dashboard";

import "./styles.scss";
import { Link } from "react-router-dom";

const selectProps: SelectProps = {
  allowDeselect: false,
};
const gridItemProps: GridProps = {
  size: {
    xl: 3,
    lg: 4,
    md: 4,
    sm: 6,
    xs: 12,
  },
};
const Courses: React.FC = () => {
  const context = useContext(AppContext);
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });

  const filterForm = useForm({
    initialValues: {
      isActive: "unspecified",
    },
    validate: {
      isActive: (value) =>
        value && value.length > 0 ? null : "Please select a role",
    },
  });
  const {
    data: plans,
    reloadData: reloadPlans,
    isLoading: isLoadingPlans,
  } = usePerformRequest<PaginatedResponse<PlanProps[]>>({
    route: Endpoints.GetPlans(1, 2000).route,
    method: Endpoints.GetPlans(1, 2000).method,
  });
  const {
    data: courses,
    reloadData: reloadCourses,
    isLoading: isLoadingCourses,
  } = usePerformRequest<PaginatedResponse<PlanProps[]>>({
    route: Endpoints.GetCourses(pagination.page, pagination.limit).route,
    method: Endpoints.GetCourses(pagination.page, pagination.limit).method,
    body:
      filterForm.values.isActive !== "unspecified"
        ? {
          isActive: filterForm.values.isActive,
        }
        : {},
  });
  const {
    data: teachers,
    reloadData: reloadTeachers,
    isLoading: isLoadingTeam,
  } = usePerformRequest<PaginatedResponse<TeacherProps[]>>({
    route: Endpoints.GetTeachers(1, 2000).route,
    method: Endpoints.GetTeachers(1, 2000).method,
  });
  useEffect(() => {
    reloadCourses();
  }, [filterForm.values.isActive]);

  const [courseImageFiles, setCourseImageFiles] = useState<File[]>([]);
  const courseForm = useForm<CourseProps>({
    initialValues: {
      id: "",
      name: "",
      description: "",
      thumbnail: "",
      images: [],
      planID: "",

      teacherHistory: [],
      isActive: false,
      createdAt: 0,
      createdBy: "",
      teacherID: "",
    },
    validate: {
      name: (value) => (value && value.length > 4 ? null : "Please enter name"),
      description: (value) =>
        value && value.length > 4 ? null : "Please enter description",

      thumbnail: (value) =>
        courseModalAction === "create"
          ? null
          : value && value.length > 0
            ? null
            : "Please enter thumbnail",
      images: (value) =>
        courseModalAction === "create"
          ? courseImageFiles.length > 0
            ? null
            : "Please upload images"
          : value.length > 0 || courseImageFiles.length > 0
            ? null
            : "Please upload images",
      planID: (value) =>
        value && value.length > 4 ? null : "Please select a plan",
    },
  });
  const [courseModalAction, setCourseModalAction] = useState<
    "create" | "update" | "view"
  >("create");

  const [currentCourse, setCurrentCourse] = useState<CourseProps>();

  const [
    isCourseModalOpened,
    { open: OpenCourseModal, close: CloseCourseModal },
  ] = useDisclosure(false);

  const [isCreatingOrUpdatingCourse, setCreatingOrUpdatingCourse] =
    useState<boolean>(false);

  const runCourseAction = async (data: any) => {
    const r = await PerformRequest({
      method: Endpoints.GetCreateCourse().method,
      route: Endpoints.GetCreateCourse().route,
      data,
      callback: () => {
        setCreatingOrUpdatingCourse(false);
      },
    });
    setCreatingOrUpdatingCourse(false);
    if (r) {
      if (r.status === 403) {
        showValidationErrors(r);
      }
      if (r.status === 201) {
        CancelCreateCourse();
        reloadCourses();
        DefaultSuccessNotification("Course created successfully!");
      }
    }
  };
  const CreateNewCourse = async () => {
    const { hasErrors: formHasErrors } = courseForm.validate();
    const { name, description, planID } = courseForm.values;

    if (!formHasErrors) {
      const data = {
        name,
        description,
        planID,
      };
      setCreatingOrUpdatingCourse(true);

      try {
        const uploaded = await UploadFiles(courseImageFiles as any, false, "r2");

        if (uploaded && uploaded.status === 201) {
          runCourseAction({
            ...data,
            images: uploaded.data.map((image) => image.path),
          });
        } else {
          setCreatingOrUpdatingCourse(false);
          DefaultErrorNotification("Could not upload image!");
        }
      } catch (error) {
        setCreatingOrUpdatingCourse(false);
        DefaultErrorNotification("Could not upload image!");
      }
    }
  };
  const UpdateCourse = async () => {
    const { hasErrors: formHasErrors, errors } = courseForm.validate();
    if (errors.thumbnail) {
      DefaultErrorNotification("You must select ONE image as the thumbnail!");
      return;
    }
    // @ts-ignore
    const {
      name,
      description,
      thumbnail,
      images,
      planID,
      id: courseID,
    } = courseForm.values;

    if (!formHasErrors) {
      setCreatingOrUpdatingCourse(true);
      const r = await PerformRequest({
        method: Endpoints.GetUpdateCourse(courseID).method,
        route: Endpoints.GetUpdateCourse(courseID).route,
        data: {
          name,
          description,
          thumbnail,
          images,
          planID,
        },
        callback: () => {
          setCreatingOrUpdatingCourse(false);
        },
      });
      setCreatingOrUpdatingCourse(false);
      if (r) {
        if (r.status === 200) {
          CancelCreateCourse();
          reloadCourses();
          DefaultSuccessNotification("Course has been updated!");
        }
      }
    }
  };

  const [deletingMemberCourse, setDeletingMemberCourse] = useState<string>("");
  const DeleteCourse = async (id: string) => {
    // @ts-ignore
    setDeletingMemberCourse(id);
    const r = await PerformRequest({
      method: Endpoints.GetDeleteCourse(id).method,
      route: Endpoints.GetDeleteCourse(id).route,
      data: {
        // DELETE RESOURCES INCLUDING COURSE AND LESSONS
        deleteResources: false,
      },
      callback: () => {
        setCreatingOrUpdatingCourse(false);
        setDeletingMemberCourse("");
      },
    });
    setDeletingMemberCourse("");
    setCreatingOrUpdatingCourse(false);
    if (r) {
      if (r.status === 200) {
        CancelCreateCourse();
        reloadCourses();
        DefaultSuccessNotification("Course has been REMOVED!");
      }
    }
  };

  const ToggleCourseStatus = async (course: CourseProps) => {
    setCreatingOrUpdatingCourse(true);
    const r = await PerformRequest({
      method: Endpoints.GetToggleCourseStatus(course.id).method,
      route: Endpoints.GetToggleCourseStatus(course.id).route,
      data: {
        isActive: !course.isActive,
      },
      callback: () => {
        setCreatingOrUpdatingCourse(false);
        reloadCourses();
      },
    });
    setCreatingOrUpdatingCourse(false);
    if (r) {
      if (r.status === 200) {
        setCurrentCourse({
          ...(currentCourse as CourseProps),
          isActive: !course.isActive,
        });
        reloadCourses();
        CloseCourseModal();
        DefaultSuccessNotification("Course status has been updated!");
      }
    }
  };

  const CancelCreateCourse = () => {
    courseForm.reset();
    setDeletingMemberCourse("");
    CloseCourseModal();
    setCourseImageFiles([]);
  };

  const getCourseTeacherName = (teacherID: string) => {
    if (teacherID && teacherID.length > 1) {
      const teacher = teachers?.data.find((t) => t.id === teacherID);
      return teacher?.name;
    }
    return "Not assigned";
  };
  const courseFormInputProps = {
    my: 5,
  };

  const textProps: TextProps = {
    fz: 13,
    className: "table-value",
  };
  const showImageCarousel =
    courseImageFiles.length > 0 || courseForm.values.images.length > 0;
  return (
    <Container maxWidth="lg">
      <div className="courses-container">
        <div className="component-header flex-row align-end justify-between">
          <Text className="label">Courses</Text>
          <div className="flex-row align-center">
            <Button
              color={Colors.PurplePrimary}
              rightSection={<IconPlus size={16} />}
              mr={10}
              onClick={() => {
                OpenCourseModal();
                setCourseModalAction("create");
                courseForm.reset();
              }}
              disabled={isCreatingOrUpdatingCourse || isLoadingCourses}
            >
              Add Course
            </Button>
            <Button
              color={Colors.PurplePrimary}
              variant="outline"
              onClick={reloadCourses}
              loading={isLoadingCourses}
              rightSection={<IconReload size={16} />}
            >
              Reload
            </Button>
          </div>
        </div>
        <Divider w="100%" my={10} />
        <Select
          w={200}
          {...filterForm.getInputProps("isActive")}
          {...selectProps}
          label="Filter by status"
          data={[
            ...YesOrNoList.map((item) => {
              return {
                value: item.value,
                label: item.label,
              };
            }),
            {
              value: "unspecified",
              label: "All",
            },
          ]}
        />

        <div className="courses">
          {isLoadingCourses ? (
            <MegaLoader
              size={50}
              containerStyle={{
                marginTop: 45,
                marginBottom: 45,
              }}
            />
          ) : (
            <>
              {!courses?.data || courses?.data.length === 0 ? (
                <DisplayAlert
                  title="No Courses Found"
                  children={<Text>No courses found!</Text>}
                />
              ) : (
                <DatagridDisplay
                  rows={courses?.data.filter(
                    (t) => t.id !== context?.profile?.id
                  )}
                  isLoading={isLoadingCourses}
                  columns={[
                    {
                      field: "createdAt",
                      flex: 1,
                      headerName: "Creation Date",
                      renderCell: (
                        params: GridRenderCellParams<CourseProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <Text {...textProps}>
                            {dayjs(params.row.createdAt).format(
                              "ddd, MMMM DD YYYY"
                            )}
                          </Text>
                        </div>
                      ),
                    },
                    {
                      field: "name",
                      flex: 1,
                      headerName: "Name",
                      renderCell: (
                        params: GridRenderCellParams<CourseProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <Text {...textProps}>{params.row.name}</Text>
                        </div>
                      ),
                    },
                    {
                      field: "planID",
                      flex: 1,
                      headerName: "Plan",
                      renderCell: (
                        params: GridRenderCellParams<CourseProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <Text {...textProps}>
                            {
                              plans?.data.find(
                                (p) => p.id === params.row.planID
                              )?.name
                            }
                          </Text>
                        </div>
                      ),
                    },

                    {
                      field: "isActive",
                      flex: 1,
                      headerName: "Status",
                      renderCell: (
                        params: GridRenderCellParams<CourseProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <IconCircleFilled
                            size={10}
                            color={
                              params.row.isActive
                                ? Colors.GreenPrimary
                                : Colors.RedPrimary
                            }
                          />
                          <Text
                            {...textProps}
                            fw={600}
                            ml={3}
                            c={
                              params.row.isActive
                                ? Colors.GreenPrimary
                                : Colors.RedPrimary
                            }
                          >
                            {params.row.isActive ? "Active" : "Inactive"}
                          </Text>
                        </div>
                      ),
                    },
                    {
                      headerName: "Actions",
                      field: "id",
                      flex: 1,
                      renderCell: (
                        params: GridRenderCellParams<CourseProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <div className="flex-row align-center">
                            <ActionIcon
                              variant="subtle"
                              onClick={() => {
                                OpenCourseModal();
                                setCourseModalAction("update");
                                // @ts-ignore
                                courseForm.setValues({
                                  id: params.row.id,
                                  name: params.row.name,
                                  description: params.row.description,
                                  thumbnail: params.row.thumbnail,
                                  images: params.row.images,
                                  planID: params.row.planID,
                                });
                              }}
                            >
                              <IconEdit size={18} />
                            </ActionIcon>
                            &nbsp; &nbsp;
                            <ActionIcon
                              variant="subtle"
                              onClick={() => {
                                OpenCourseModal();
                                setCourseModalAction("view");

                                setCurrentCourse(params.row);
                              }}
                            >
                              <IconEye size={18} />
                            </ActionIcon>
                            &nbsp; &nbsp;
                            <Popover
                              width={200}
                              position="bottom"
                              withArrow
                              shadow="md"
                            >
                              <Popover.Target>
                                <ActionIcon
                                  variant="subtle"
                                  color={Colors.RedPrimary}
                                >
                                  <IconTrash size={18} />
                                </ActionIcon>
                              </Popover.Target>
                              <Popover.Dropdown>
                                <Text size="xs" c={Colors.RedPrimary}>
                                  This action CANNOT be undone!
                                </Text>
                                <Button
                                  color={Colors.RedPrimary}
                                  mt={5}
                                  w="100%"
                                  onClick={() => {
                                    DeleteCourse(params.row.id);
                                  }}
                                  loading={
                                    deletingMemberCourse === params.row.id
                                  }
                                  disabled={
                                    deletingMemberCourse.length > 0 &&
                                    deletingMemberCourse !== params.row.id
                                  }
                                >
                                  Delete
                                </Button>
                              </Popover.Dropdown>
                            </Popover>
                          </div>
                        </div>
                      ),
                    },
                  ]}
                />
              )}
            </>
          )}
        </div>
      </div>
      <Modal
        opened={isCourseModalOpened}
        onClose={isCreatingOrUpdatingCourse ? () => { } : CancelCreateCourse}
        centered
        size="lg"
        withCloseButton={false}
      >
        <div className="course-modal flex-col">
          <div className="flex-row align-center width-100 justify-between">
            <div className="flex-row align-center">
              <Text tt="capitalize" c={Colors.BluePrimary} fw={500} fz={17}>
                {courseModalAction} Course
              </Text>
            </div>
            <ActionIcon
              variant="outline"
              color={Colors.BluePrimary}
              onClick={CancelCreateCourse}
            >
              <IconX color={Colors.BluePrimary} size={18} />
            </ActionIcon>
          </div>
          {courseModalAction === "view" ? (
            <>
              {currentCourse && (
                <>
                  <Table
                    mt={20}
                    variant="vertical"
                    layout="fixed"
                    withTableBorder
                  >
                    <Table.Thead>
                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th w={200}>Description</Table.Th>
                        <Table.Td>
                          <CopyButton value={currentCourse.description}>
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? "Copied!" : "Copy to clipboard"}
                              >
                                <div
                                  className="pointer flex-row align-center"
                                  onClick={copy}
                                  style={{
                                    color: copied
                                      ? Colors.PurplePrimary
                                      : Colors.BluePrimary,
                                    height: "100%",
                                  }}
                                >
                                  {getEllipsisFromString(
                                    currentCourse.description,
                                    20
                                  )}
                                </div>
                              </Tooltip>
                            )}
                          </CopyButton>
                        </Table.Td>
                      </Table.Tr>

                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th>Name</Table.Th>
                        <Table.Td>{currentCourse.name}</Table.Td>
                      </Table.Tr>

                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th>Creation Date</Table.Th>
                        <Table.Td>
                          {dayjs(currentCourse.createdAt).format(
                            "ddd, MMMM DD YYYY"
                          )}
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th>Teacher</Table.Th>
                        <Table.Td>
                          {getCourseTeacherName(currentCourse.teacherID)}
                        </Table.Td>
                      </Table.Tr>
                    </Table.Thead>
                  </Table>
                  <div className="flex-row width-100 align-center">
                    <Button
                      mt={20}
                      w={160}
                      title={
                        currentCourse?.teacherID
                          ? currentCourse.teacherID?.length === 0
                            ? "You must assign a teacher to the course before you can activate it!"
                            : ""
                          : "You must assign a teacher to the course before you can activate it!"
                      }
                      loading={isCreatingOrUpdatingCourse}
                      disabled={
                        currentCourse?.teacherID
                          ? currentCourse.teacherID?.length === 0
                          : true
                      }
                      color={
                        currentCourse.isActive
                          ? Colors.RedPrimary
                          : Colors.GreenPrimary
                      }
                      onClick={() => ToggleCourseStatus(currentCourse)}
                    >
                      {currentCourse.isActive ? "Deactivate" : "Activate"}
                    </Button>
                    &nbsp; &nbsp;
                    <Link to={`/d/course/${currentCourse.id}`}>
                      <Button mt={20} w={160} color={Colors.PurplePrimary}>
                        Course Details
                      </Button>
                    </Link>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="flex-row align-center justify-between width-100">
                <TextInput
                  {...courseForm.getInputProps("name")}
                  {...courseFormInputProps}
                  label="Name"
                  placeholder="Course Name"
                  spellCheck={false}
                  w="48%"
                />
                <Select
                  {...courseForm.getInputProps("planID")}
                  {...courseFormInputProps}
                  label="Plan"
                  w="48%"
                  placeholder="Select a plan"
                  data={
                    plans?.data.map((plan) => ({
                      label: plan.name,
                      value: plan.id,
                    })) || []
                  }
                />
              </div>

              <Textarea
                {...courseForm.getInputProps("description")}
                {...courseFormInputProps}
                label="Description"
                placeholder="Enter course description"
                w="100%"
                rows={5}
              />
              {showImageCarousel && (
                <Carousel
                  withControls={true}
                  withIndicators={true}
                  slideSize="70%"
                  height={200}
                  slideGap="md"
                  controlsOffset="sm"
                  controlSize={26}
                  className="carousel-container"
                >
                  {courseImageFiles.map((file) => (
                    <Carousel.Slide key={generateRandomString(12)}>
                      <div
                        className="carousel-image-container"
                        style={{
                          backgroundImage: `url(${URL.createObjectURL(file)})`,
                        }}
                      >
                        <div className="flex-row align-center justify-between">
                          <ActionIcon
                            variant="filled"
                            color={Colors.RedPrimary}
                            size={30}
                            onClick={() => {
                              setCourseImageFiles(
                                courseImageFiles.filter((f) => f !== file)
                              );
                              if (file.name === courseForm.values.thumbnail) {
                                courseForm.setFieldValue("thumbnail", "");
                              }
                            }}
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                          <Checkbox
                            display={
                              courseModalAction === "create" ? "none" : "block"
                            }
                            checked={file.name === courseForm.values.thumbnail}
                            onChange={() => {
                              courseForm.setFieldValue(
                                "thumbnail",
                                file.name === courseForm.values.thumbnail
                                  ? ""
                                  : file.name
                              );
                            }}
                          />
                        </div>
                      </div>
                    </Carousel.Slide>
                  ))}
                  {courseModalAction === "update" && (
                    <>
                      {courseForm.values.images.map((image) => (
                        <Carousel.Slide key={generateRandomString(12)}>
                          <div
                            className="carousel-image-container"
                            style={{
                              backgroundImage: `url(${image})`,
                            }}
                          >
                            <div className="flex-row align-center justify-between">
                              <ActionIcon
                                variant="filled"
                                color={Colors.RedPrimary}
                                size={30}
                                onClick={() => {
                                  courseForm.setFieldValue(
                                    "images",
                                    courseForm.values.images.filter(
                                      (i) => i !== image
                                    )
                                  );
                                  if (image === courseForm.values.thumbnail) {
                                    courseForm.setFieldValue("thumbnail", "");
                                  }
                                }}
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                              <Checkbox
                                checked={courseForm.values.thumbnail === image}
                                onChange={() => {
                                  courseForm.setFieldValue(
                                    "thumbnail",
                                    courseForm.values.thumbnail === image
                                      ? ""
                                      : image
                                  );
                                }}
                              />
                            </div>
                          </div>
                        </Carousel.Slide>
                      ))}
                    </>
                  )}
                </Carousel>
              )}
              <Dropzone
                mt={20}
                onDrop={(files) => {
                  setCourseImageFiles([...courseImageFiles, ...files]);
                  console.log(courseImageFiles);
                }}
                onReject={(files) => console.log("rejected files", files)}
                maxSize={5 * 1024 ** 2}
                accept={IMAGE_MIME_TYPE}
                styles={{
                  root: {
                    borderColor: courseForm.errors.images
                      ? Colors.RedPrimary
                      : Colors.GrayTertiary,
                  },
                }}
              >
                <Group
                  justify="center"
                  gap="xl"
                  mih={50}
                  style={{ pointerEvents: "none" }}
                >
                  <div className="flex-col align-center">
                    <IconPhoto size={40} color={Colors.GraySecondary} />
                    <Text ta="center" size="lg" inline>
                      Drag images here or click to select images
                    </Text>
                    <Text ta="center" size="xs" c="dimmed" inline mt={7}>
                      Attach as many images as you like, each image should not
                      exceed 5mb
                    </Text>
                  </div>
                </Group>
              </Dropzone>

              <div className="flex-row width-100 justify-between align-center">
                <Button
                  mt={10}
                  w="48%"
                  color={Colors.PurplePrimary}
                  onClick={
                    courseModalAction === "create"
                      ? CreateNewCourse
                      : UpdateCourse
                  }
                  loading={isCreatingOrUpdatingCourse}
                  tt="capitalize"
                >
                  {courseModalAction}
                </Button>
                <Button
                  mt={10}
                  w="48%"
                  variant="outline"
                  onClick={CancelCreateCourse}
                  disabled={isCreatingOrUpdatingCourse}
                  color={Colors.RedPrimary}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </Container>
  );
};

export default Courses;
